import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { InteractionOperationalState } from '../conversations/interaction-operational-contracts'
import type { InteractionOperationalRepository } from '../conversations/interaction-operational-repository'
import type { PatientRecord } from './patients-contracts'

export function usePatientOperationalStates(
  repository: InteractionOperationalRepository | undefined,
  tenantId: string,
  patients: PatientRecord[]
) {
  const generation = useRef(0)
  const [states, setStates] = useState<
    Record<string, InteractionOperationalState>
  >({})
  const aggregateIds = useMemo(
    () => [
      ...new Set(
        patients.flatMap((patient) =>
          patient.interactionAggregateId ? [patient.interactionAggregateId] : []
        )
      ),
    ],
    [patients]
  )
  const key = aggregateIds.join(':')

  const refresh = useCallback(async () => {
    if (!repository || aggregateIds.length === 0) {
      setStates({})
      return
    }
    const current = ++generation.current
    try {
      const batches = Array.from(
        { length: Math.ceil(aggregateIds.length / 100) },
        (_, index) => aggregateIds.slice(index * 100, (index + 1) * 100)
      )
      const items = (
        await Promise.all(
          batches.map((batch) => repository.query(tenantId, batch))
        )
      ).flat()
      if (generation.current === current) {
        setStates(
          Object.fromEntries(items.map((item) => [item.aggregateId, item]))
        )
      }
    } catch {
      if (generation.current === current) setStates({})
    }
  }, [key, repository, tenantId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void refresh()
    return () => {
      generation.current += 1
    }
  }, [refresh])

  const update = useCallback((state: InteractionOperationalState) => {
    setStates((current) => ({ ...current, [state.aggregateId]: state }))
  }, [])

  return { states, refresh, update }
}
